/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

/**
 * File system operations for reading and writing files in a repository
 * 
 * Note: This uses the File System Access API which requires user permission
 * and only works in modern browsers (Chrome, Edge, etc.)
 */

export interface FileHandle {
  name: string
  kind: 'file' | 'directory'
  handle: FileSystemFileHandle | FileSystemDirectoryHandle
}

/**
 * Fix common UTF-8 double-encoding issues
 * This happens when files are saved with UTF-8 but read/interpreted as Latin-1
 */
export function fixDoubleEncoding(content: string): string {
  return content
    .replace(/\u00C2\u00A9/g, '\u00A9')  // Copyright symbol
    .replace(/\u00C2\u00AE/g, '\u00AE')  // Registered trademark
    .replace(/\u00E2\u0080\u0094/g, '\u2014')  // Em dash
    .replace(/\u00E2\u0080\u0093/g, '\u2013')  // En dash
    .replace(/\u00E2\u0080\u00A2/g, '\u2022')  // Bullet
    .replace(/\u00E2\u0080\u0098/g, '\u2018')  // Left single quote
    .replace(/\u00E2\u0080\u0099/g, '\u2019')  // Right single quote
    .replace(/\u00E2\u0080\u009C/g, '\u201C')  // Left double quote
    .replace(/\u00E2\u0080\u009D/g, '\u201D')  // Right double quote
    .replace(/\u00E2\u0080\u00A6/g, '\u2026')  // Ellipsis
    .replace(/\u00C3\u0097/g, '\u00D7')  // Multiplication sign
    .replace(/\u00C3\u00B7/g, '\u00F7')  // Division sign
}

/**
 * Request access to a directory
 */
export async function requestDirectoryAccess(): Promise<FileSystemDirectoryHandle | null> {
  try {
    // @ts-ignore - File System Access API
    const dirHandle = await window.showDirectoryPicker({
      mode: 'readwrite',
    })
    return dirHandle
  } catch (error) {
    console.error('Failed to access directory:', error)
    return null
  }
}

/**
 * Read a file from a directory handle
 */
export async function readFile(
  dirHandle: FileSystemDirectoryHandle,
  filePath: string
): Promise<string | null> {
  try {
    const pathParts = filePath.split('/')
    let currentHandle: FileSystemDirectoryHandle | FileSystemFileHandle = dirHandle

    // Navigate through directories
    for (let i = 0; i < pathParts.length - 1; i++) {
      currentHandle = await (currentHandle as FileSystemDirectoryHandle).getDirectoryHandle(pathParts[i])
    }

    // Get the file
    const fileName = pathParts[pathParts.length - 1]
    const fileHandle = await (currentHandle as FileSystemDirectoryHandle).getFileHandle(fileName)
    const file = await fileHandle.getFile()

    // Read file as ArrayBuffer and decode as UTF-8 to ensure proper encoding
    const arrayBuffer = await file.arrayBuffer()
    const decoder = new TextDecoder('utf-8')
    const content = decoder.decode(arrayBuffer)

    // Fix common double-encoding issues
    return fixDoubleEncoding(content)
  } catch (error) {
    console.error(`Failed to read file ${filePath}:`, error)
    return null
  }
}

/**
 * Write a file to a directory handle with retry logic
 */
export async function writeFile(
  dirHandle: FileSystemDirectoryHandle,
  filePath: string,
  content: string,
  retries: number = 3
): Promise<boolean> {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const pathParts = filePath.split('/')
      let currentHandle: FileSystemDirectoryHandle = dirHandle

      // Navigate/create directories
      for (let i = 0; i < pathParts.length - 1; i++) {
        try {
          currentHandle = await currentHandle.getDirectoryHandle(pathParts[i])
        } catch {
          currentHandle = await currentHandle.getDirectoryHandle(pathParts[i], { create: true })
        }
      }

      // Create/update the file - get fresh handle on each attempt
      const fileName = pathParts[pathParts.length - 1]
      const fileHandle = await currentHandle.getFileHandle(fileName, { create: true })

      // Request permission if needed
      // @ts-ignore
      if ((await fileHandle.queryPermission({ mode: 'readwrite' })) !== 'granted') {
        // @ts-ignore
        if ((await fileHandle.requestPermission({ mode: 'readwrite' })) !== 'granted') {
          throw new Error('Permission denied')
        }
      }

      // Use keepExistingData option to avoid swap file issues
      const writable = await fileHandle.createWritable({ keepExistingData: false })
      await writable.write(content)
      await writable.close()

      return true
    } catch (error: any) {
      console.warn(`Write attempt ${attempt + 1}/${retries} failed for ${filePath}:`, error.name, error.message)

      // If it's the last attempt or a non-retryable error, fail
      if (attempt === retries - 1 ||
        (error.name !== 'AbortError' && error.name !== 'InvalidStateError' && error.name !== 'NotAllowedError')) {
        console.error(`Failed to write file ${filePath} after ${attempt + 1} attempts:`, error)
        return false
      }

      // Wait a bit before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, attempt)))
    }
  }

  return false
}

/**
 * List files in a directory recursively
 */
export async function listFiles(
  dirHandle: FileSystemDirectoryHandle,
  path: string = '',
  autoCleanZoneIdentifiers: boolean = true
): Promise<string[]> {
  const files: string[] = []

  try {
    // @ts-ignore
    for await (const entry of dirHandle.values()) {
      const entryPath = path ? `${path}/${entry.name}` : entry.name

      if (entry.kind === 'file') {
        // Auto-delete Zone.Identifier files created by WSL/Windows
        if (entry.name.endsWith(':Zone.Identifier') && autoCleanZoneIdentifiers) {
          try {
            await dirHandle.removeEntry(entry.name)
            console.log(`[Files] Auto-deleted Zone.Identifier: ${entryPath}`)
          } catch (error) {
            console.warn(`[Files] Failed to delete Zone.Identifier: ${entryPath}`, error)
          }
          continue // Skip adding to file list
        }

        files.push(entryPath)
      } else if (entry.kind === 'directory') {
        // Skip common directories to ignore
        if (['.git', 'node_modules', '.next', 'dist', 'build', '.vscode'].includes(entry.name)) {
          continue
        }
        const subFiles = await listFiles(entry, entryPath, autoCleanZoneIdentifiers)
        files.push(...subFiles)
      }
    }
  } catch (error) {
    console.error('Failed to list files:', error)
  }

  return files
}

/**
 * Check if a file exists
 */
export async function fileExists(
  dirHandle: FileSystemDirectoryHandle,
  filePath: string
): Promise<boolean> {
  try {
    const pathParts = filePath.split('/')
    let currentHandle: FileSystemDirectoryHandle | FileSystemFileHandle = dirHandle

    // Navigate through directories
    for (let i = 0; i < pathParts.length - 1; i++) {
      currentHandle = await (currentHandle as FileSystemDirectoryHandle).getDirectoryHandle(pathParts[i])
    }

    // Check if file exists
    const fileName = pathParts[pathParts.length - 1]
    await (currentHandle as FileSystemDirectoryHandle).getFileHandle(fileName)
    return true
  } catch {
    return false
  }
}

/**
 * Delete a file
 */
export async function deleteFile(
  dirHandle: FileSystemDirectoryHandle,
  filePath: string
): Promise<boolean> {
  try {
    const pathParts = filePath.split('/')
    let currentHandle: FileSystemDirectoryHandle = dirHandle

    // Navigate through directories
    for (let i = 0; i < pathParts.length - 1; i++) {
      currentHandle = await currentHandle.getDirectoryHandle(pathParts[i])
    }

    // Delete the file
    const fileName = pathParts[pathParts.length - 1]
    await currentHandle.removeEntry(fileName)
    return true
  } catch (error) {
    console.error(`Failed to delete file ${filePath}:`, error)
    return false
  }
}

